import React, { useState, useEffect, forwardRef } from "react";
import { useSelector } from "react-redux";

/** Utilities */
import formatDate from "../../../../../../lib/formatDate";

/** CSS Files */
import "./TaskTimeLog.scss";

/** Common Components */
import Label from "../../../../../../components/ui/label/Label";
import Buttons from "../../../../../../components/ui/button/Buttons";
import Iconify from "../../../../../../layouts/components/iconify/iconify";
import DropdownSelect from "../../../../../../components/ui/dropdown/DropdownSelect";
import TextArea from "../../../../../../components/finalForms/ui/inputs/textArea/TextArea";
import DatePickerComponent from "../../../../../../components/finalForms/ui/inputs/datePicker/DatePicker";

/** Common Services & Data Files */
import SwalAlert from "../../../../../../services/swalService/SwalService";
import ToastService from "../../../../../../services/toastService/ToastService";

/** RTK Query */
import { useAddTimeSheetRecordMutation } from "../../../../../../app/services/timeSheetLogAPI";
import { useGetAllTimeLogSubActivityMutation, useLazyGetAllTimeLogActivityQuery } from "../../../../../../app/services/commonAPI";

const TaskTimeLog = (props, ref) => {

  const [selectActivity, setSelectActivity] = useState(null);
  const [activityList, setActivityList] = useState([]);
  const [selectSubActivity, setSelectSubActivity] = useState(null);
  const [subActivityList, setSubActivityList] = useState([]);
  const [workAtData, setWorkAtData] = useState(new Date());
  const [endTimeData, setEndTimeData] = useState();
  const [desciptionData, setDesciptionData] = useState();
  const [startTimeData, setStartTimeData] = useState(new Date());
  const [totalTime, setTotalTime] = useState("");
  const UserId = useSelector(item => item.auth.user.userId)

  const { error } = SwalAlert();

  const filterPassedTime = (time) => {

    if (!startTimeData) return true;
    const currentDate = new Date(startTimeData);
    const selectedDate = new Date(time);

    return currentDate.getTime() <= selectedDate.getTime();
  };

  const [getAllTimeLogActivity, {
    isFetching: isActivityFetching,
    isSuccess: isActivityFetched,
    data: timeLogActivityData
  }] = useLazyGetAllTimeLogActivityQuery();

  const [getAllTimeSubLogActivity, {
    isFetching: isSubActivityFetching,
    isSuccess: isSubActivityFetched,
    data: timeLogSubActivityData
  }] = useGetAllTimeLogSubActivityMutation();


  const [addTimeSheetRecord, {
    isLoading: isTimeSheetRecordAdding,
    isSuccess: isTimeSheetRecordAdded,
    data: addTimeSheetRecordData
  }] = useAddTimeSheetRecordMutation();


  const calculateTotalTime = () => {
    if (startTimeData && endTimeData) {
      const start = new Date(startTimeData);
      const end = new Date(endTimeData);

      let timeDifference = end - start;

      if (timeDifference < 0) {
        const fullDayInMs = 24 * 60 * 60 * 1000;
        timeDifference += fullDayInMs;
      }

      // Set the total time
      setTotalTime(formatTimeDifference(timeDifference));
    }
  };

  const formatTimeDifference = (differenceInMs) => {
    const totalMinutes = Math.floor(differenceInMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  useEffect(() => {
    calculateTotalTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimeData, endTimeData]);


  const handleSaveClick = () => {

    if (!selectSubActivity || !selectSubActivity.value ||
      !selectActivity || !selectActivity?.value ||
      !workAtData || !startTimeData || !endTimeData || !desciptionData) {
      error("Please fill all the required fields.", "Validation Failed!");
      return;
    }

    const startTime = formatDate(startTimeData, "HH:mm:ss");
    const endTime = formatDate(endTimeData, "HH:mm:ss");

    if (startTime >= endTime) {
      error("Start time must be earlier than end time.", "Invalid Time Range!");
      return;
    }

    const req = {
      timesheetDetailId: props?.editData?.timesheetDetailId || 0,
      // timesheetId: timesheetId,
      userId: UserId,
      taskId: props.taskId,
      projectId: props.projectId,
      timeLogActivityId: selectActivity.value,
      timeLogSubActivityId: selectSubActivity.value,
      startTime: startTime,
      endTime: endTime,
      WorkDescription: desciptionData,
      workAt: workAtData,
    };

    addTimeSheetRecord(req);  // Call API
  };


  useEffect(() => {
    if (props.isEdit && props.editData) {

      const { startTime, endTime, workDescription, workAt } = props.editData;
      setDesciptionData(workDescription);

      if (startTime) {
        const [startHours, startMinutes, startSeconds] = startTime.split(":").map(Number);
        const startDate = new Date();
        startDate.setHours(startHours, startMinutes, startSeconds, 0);
        setStartTimeData(startDate);
      }

      if (endTime) {
        const [endHours, endMinutes, endSeconds] = endTime.split(":").map(Number);
        const endDate = new Date();
        endDate.setHours(endHours, endMinutes, endSeconds, 0);
        setEndTimeData(endDate);
      }
      setWorkAtData(workAt ? new Date(workAt) : workAt);
    }
  }, [props.isEdit, props.editData]);

  const handalactivity = (options) => {
    setSelectActivity(options);
    setSelectSubActivity(null);
    if (options) {
      getAllTimeSubLogActivity({ timeLogActivityId: options.value })
    }
    if (timeLogSubActivityData) {
      const subActivityListData = timeLogSubActivityData.map((item) => ({
        value: item.timeLogSubActivityId,
        label: item.subActivityName,
      }));
      setSubActivityList(subActivityListData);
    }
  }

  // initial API calls
  useEffect(() => {
    if (props) {
      getAllTimeLogActivity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);


  // Activity Dropdown
  useEffect(() => {
    if (!isActivityFetching && isActivityFetched && timeLogActivityData) {
      const activityListData = timeLogActivityData.map((item) => ({
        value: item.timeLogActivityId,
        label: item.activityName,
      }));
      setActivityList(activityListData);
      if (props) {
        const selectedActivity = activityListData.find(activity => activity.value === props?.editData?.timeLogActivityId);
        setSelectActivity(selectedActivity || null);
        getAllTimeSubLogActivity({ timeLogActivityId: props?.editData?.timeLogActivityId })
      }
      if (props.isModelOpen && activityListData) {
        const defaultActivity = activityListData?.find(activity => activity.value === 1);
        if (defaultActivity) {
          setSelectActivity(defaultActivity);
          handalactivity(defaultActivity);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivityFetching, isActivityFetched, timeLogActivityData]);

  //   Sub Activity Dropdown
  useEffect(() => {
    if (!isSubActivityFetching && isSubActivityFetched && timeLogSubActivityData) {
      const subActivityListData = timeLogSubActivityData.map((item) => ({
        value: item.timeLogSubActivityId,
        label: item.subActivityName,
      }));
      setSubActivityList(subActivityListData);
      if (props) {
        const selectedSubActivity = subActivityListData.find(subActivity => subActivity.value === props?.editData?.timeLogSubActivityId);
        setSelectSubActivity(selectedSubActivity || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubActivityFetching, isSubActivityFetched, timeLogSubActivityData]);

  const handleDateChange = (date) => {
    setWorkAtData(date)
  }

  const handlestarttimeDateChange = (data) => {
    setStartTimeData(data);
  }

  const handleEndDateTimeChange = (data) => {
    setEndTimeData(data)
  }

  const handaltextarea = (data) => {
    setDesciptionData(data)
  }

  const filterPassedDate = (date) => {
    if (!workAtData) return true;
    const currentDate = new Date();
    const selectedDate = new Date(date);
    return selectedDate.getTime() <= currentDate.getTime();
  };

  useEffect(() => {
    if (isTimeSheetRecordAdded && addTimeSheetRecordData) {
      if (addTimeSheetRecordData.errorMessage.includes("exists")) {
        ToastService.warning(addTimeSheetRecordData.errorMessage);
        return;
      } else {
        ToastService.success(addTimeSheetRecordData.errorMessage);
        setStartTimeData(endTimeData || new Date());
        setEndTimeData(undefined);
        setDesciptionData("");
        setTotalTime("");
        if (props.onSuccess) {
          props.onSuccess();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimeSheetRecordAdded, addTimeSheetRecordData]);


  return (
    <div className="task-time-log-section">
      <div className="task-time-log-title">
        Task Time{" "}
        <span className="hours">
          {" "}
          <Iconify icon="hugeicons:clock-04" /> {totalTime} Time
        </span>
      </div>
      <div className="task-time-log-container">
        <div className="row">
          <div className="col-12 validation-left-0 custom-field">
            <Label labelName="Activity" isRequired={true} />
            <DropdownSelect
              optionsValue={activityList}
              placeholder={'Select Activity'}
              value={selectActivity}
              handleDropdownChange={handalactivity}
            />
          </div>
          <div className="col-12 validation-left-0 custom-field">
            <Label labelName="Sub Activity" isRequired={true} />
            <DropdownSelect
              optionsValue={subActivityList}
              placeholder={"Select Sub Activity"}
              value={selectSubActivity}
              handleDropdownChange={(option) => setSelectSubActivity(option)}
            />
          </div>

          <div className="col-12 validation-left-0 custom-field">
            <Label labelName="Work Date" isRequired={true} />
            <DatePickerComponent
              isDisable={false}
              selected={new Date(workAtData)}
              onChange={handleDateChange}
              placeholder={"Enter Work Start Time"}
              datePickerConfig={{
                filteredDate: filterPassedDate,
                timeInputLabel: "Enter Work Start Time:",
                timeFormat: "p",
                timeIntervals: 15,
                format: "dd/MM/yyyy",
              }}
            />
          </div>
          <div className="col-6 validation-left-0 custom-field">
            <Label labelName="Start Time" isRequired={true} />
            <DatePickerComponent
              isDisable={false}
              selected={startTimeData}
              onChange={handlestarttimeDateChange}
              placeholder={"Enter Start Time"}
              datePickerConfig={{
                timeInputLabel: "Time:",
                displayTimeSelect: true,
                timeFormat: "p",
                timeIntervals: 15,
                displayTimeSelectOnly: true,
                format: "h:mm aa",
              }}
            />
          </div>
          <div className="col-6 validation-left-0 custom-field">
            <Label labelName="End Time" isRequired={true} />
            <DatePickerComponent
              isDisable={false}
              selected={endTimeData}
              onChange={handleEndDateTimeChange}
              placeholder={"Enter End Time"}
              datePickerConfig={{
                filteredTime: filterPassedTime,
                timeInputLabel: "Time:",
                displayTimeSelect: true,
                timeFormat: "p",
                timeIntervals: 15,
                displayTimeSelectOnly: true,
                format: "h:mm aa",
              }}
            />
          </div>
          <div className="col-12 validation-left-0 custom-field">
            <Label labelName="Description" isRequired={true} />
            <TextArea
              placeholder="Enter Description"
              textareaRows={3}
              maxLength={200}
              value={desciptionData}
              onChange={handaltextarea}
            />
          </div>

        </div>
      </div>
      <div className="col-12 d-flex justify-content-end mt-3 button-submit">
        {/* <Buttons
            buttonTypeClassName="dark-btn"
            onClick={handleCloseClick}
            buttonText="Cancel"
          /> */}
        <Buttons
          buttonTypeClassName="theme-btn  ml-4"
          buttonText={"Save"}
          onClick={handleSaveClick}
          isLoading={isTimeSheetRecordAdding}
        />
      </div>
    </div>
  );
};

export default forwardRef(TaskTimeLog);
